import React, { useState, useRef, useCallback } from 'react';
import { Modal } from '../UI/Modal';
import { Button } from '../UI/Button';
import { importService } from '../../services/importService';
import { useToast } from '../UI/ToastProvider';

interface ImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: 'teams' | 'users';
}

const ACCEPTED_EXTENSIONS = '.xlsx,.csv';
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const entityLabels: Record<string, { title: string; templateLabel: string }> = {
  teams: { title: 'Importar Equipes', templateLabel: 'template de equipes' },
  users: { title: 'Importar Usuários', templateLabel: 'template de usuários' },
};

export const ImportModal: React.FC<ImportModalProps> = ({ isOpen, onClose, entityType }) => {
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { addToast } = useToast();

  const labels = entityLabels[entityType];

  const resetState = () => {
    setFile(null);
    setIsDragging(false);
    setUploading(false);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const validateFile = (f: File): boolean => {
    const ext = f.name.split('.').pop()?.toLowerCase();
    if (!ext || !['xlsx', 'csv'].includes(ext)) {
      addToast('Formato inválido. Use arquivos .xlsx ou .csv', 'error');
      return false;
    }
    if (f.size > MAX_FILE_SIZE) {
      addToast('Arquivo excede o tamanho máximo de 10MB', 'error');
      return false;
    }
    return true;
  };

  const handleFileSelect = (f: File) => {
    if (validateFile(f)) {
      setFile(f);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) handleFileSelect(selected);
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const dropped = e.dataTransfer.files?.[0];
    if (dropped) handleFileSelect(dropped);
  }, []);

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    try {
      const uploadFn = entityType === 'teams'
        ? importService.uploadTeamsFile
        : importService.uploadUsersFile;

      const result = await uploadFn.call(importService, file);
      addToast(result.message, 'info');
      handleClose();
    } catch (error: any) {
      const message = error.response?.data?.message || 'Erro ao enviar arquivo de importação';
      addToast(message, 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const downloadFn = entityType === 'teams'
        ? importService.downloadTeamsTemplate
        : importService.downloadUsersTemplate;

      await downloadFn.call(importService);
      addToast('Template baixado com sucesso', 'success');
    } catch {
      addToast('Erro ao baixar template', 'error');
    }
  };

  return (
    <Modal isOpen={isOpen} title={labels.title} onClose={handleClose}>
      <div className="space-y-4">
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer
            ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}
            ${file ? 'bg-green-50 border-green-300' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_EXTENSIONS}
            onChange={handleInputChange}
            className="hidden"
          />

          {file ? (
            <div className="space-y-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mx-auto text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm font-medium text-gray-700">{file.name}</p>
              <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
              <button
                type="button"
                className="text-xs text-red-500 hover:text-red-700 underline"
                onClick={(e) => {
                  e.stopPropagation();
                  setFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
              >
                Remover arquivo
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p className="text-sm text-gray-600">
                Arraste um arquivo aqui ou <span className="text-blue-600 font-medium">clique para selecionar</span>
              </p>
              <p className="text-xs text-gray-400">Formatos aceitos: .xlsx, .csv (máx. 10MB)</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between">
          <button
            type="button"
            className="text-sm text-blue-600 hover:text-blue-800 underline flex items-center gap-1"
            onClick={handleDownloadTemplate}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Baixar {labels.templateLabel}
          </button>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={handleClose} disabled={uploading}>
            Cancelar
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!file || uploading}
          >
            {uploading ? (
              <>
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Enviando...
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Importar
              </>
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
